type Row = Record<string, any>;
type Headers = Array<{ id: string; isObject?: boolean }>;

class TableUtils {
  /* eslint-disable-next-line no-use-before-define */
  static #instance: TableUtils;

  private pathCache = new Map<string, string[]>(); // "a.b.c" -> ["a","b","c"]

  private valueCache = new WeakMap<object, Map<string, any>>(); // row -> (field -> value)

  private collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

  private constructor() {}

  static GetInstance() {
    if (!TableUtils.#instance) {
      TableUtils.#instance = new TableUtils();
    }
    return TableUtils.#instance;
  }

  private getPath(field: string): string[] {
    let p = this.pathCache.get(field);
    if (!p) {
      // split once; avoids regex & substring slicing in hot path
      p = field.split('.');
      this.pathCache.set(field, p);
    }
    return p;
  }

  private getRowCache(row: Row): Map<string, any> {
    let m = this.valueCache.get(row);
    if (!m) {
      m = new Map<string, any>();
      this.valueCache.set(row, m);
    }
    return m;
  }

  extractValueFromPointer(fieldName: string, row: Row): string | number {
    // cache per (row, fieldName)
    const rc = this.getRowCache(row);
    if (rc.has(fieldName)) {
      return rc.get(fieldName);
    }

    const path = this.getPath(fieldName);
    if (path.length === 1) {
      const v = row[fieldName];
      rc.set(fieldName, v);
      return v;
    }

    let cursor: any = row;
    // Traverse path except the last key
    for (let i = 0; i < path.length - 1; i++) {
      const k = path[i];
      cursor = cursor?.[k];
      if (cursor == null) {
        rc.set(fieldName, '');
        return '';
      }
    }
    const lastKey = path[path.length - 1];
    const v = cursor?.[lastKey];
    const result =
      v === undefined || v === null
        ? ''
        : typeof v === 'string' || typeof v === 'number'
          ? v
          : (v.toString?.() ?? '');

    rc.set(fieldName, result);
    return result;
  }

  private makeKeyGetter(orderBy: string) {
    return (row: Row) => {
      // go through cache (extractValueFromPointer already caches)
      let v: any = this.extractValueFromPointer(orderBy, row);
      // normalize strings once
      if (typeof v === 'string') v = v.toLowerCase();
      return v;
    };
  }

  // Precompile filters once into fast predicates
  private buildHeaderMap(headers: Headers) {
    const map = new Map<string, { isObject?: boolean }>();
    for (const h of headers) map.set(h.id, { isObject: !!h.isObject });
    return map;
  }

  getComparator(order: 'asc' | 'desc', orderBy: string) {
    const keyGetter = this.makeKeyGetter(orderBy);
    const { collator } = this;
    const dir = order === 'desc' ? -1 : 1;

    return (a: Row, b: Row) => {
      const av = keyGetter(a);
      const bv = keyGetter(b);

      if (av === bv) return 0;
      if (av == null) return 1 * dir;
      if (bv == null) return -1 * dir;

      if (typeof av === 'number' && typeof bv === 'number') {
        return (av < bv ? -1 : 1) * dir;
      }
      if (typeof av === 'string' && typeof bv === 'string') {
        const c = collator.compare(av, bv);
        return (c < 0 ? -1 : c > 0 ? 1 : 0) * dir;
      }
      return (av < bv ? -1 : 1) * dir;
    };
  }

  private makeFilterPredicate(
    filterKey: string,
    raw: string,
    headerMap: Map<string, { isObject?: boolean }>,
  ) {
    const header = headerMap.get(filterKey) ?? { isObject: false };
    const q = raw.trim().toLowerCase();

    // Numeric filter?
    const gt = q.startsWith('>');
    const lt = q.startsWith('<');
    const num = parseFloat(q.replace(/[<>]/g, ''));

    const isNumericQuery = (gt || lt || !Number.isNaN(num)) && !header.isObject;

    if (isNumericQuery) {
      if (gt) {
        return (row: Row, getVal: (k: string, r: Row) => any) => {
          const v = parseFloat(getVal(filterKey, row));
          return !(Number.isNaN(v) || v <= num);
        };
      }
      if (lt) {
        return (row: Row, getVal: (k: string, r: Row) => any) => {
          const v = parseFloat(getVal(filterKey, row));
          return !(Number.isNaN(v) || v >= num);
        };
      }
      return (row: Row, getVal: (k: string, r: Row) => any) => {
        const v = parseFloat(getVal(filterKey, row));
        return !(Number.isNaN(v) || v !== num);
      };
    }

    // Text/date filter
    const isDateLike =
      filterKey === 'time' || filterKey === 'createdOn' || filterKey === 'pClass.time';

    if (isDateLike) {
      return (row: Row, getVal: (k: string, r: Row) => any) => {
        const rawV = getVal(filterKey, row);
        if (rawV === undefined || rawV === null) return false;
        const s = this.formatDate(String(rawV)).toLowerCase();
        return s.indexOf(q) !== -1;
      };
    }

    if (!header.isObject) {
      return (row: Row, getVal: (k: string, r: Row) => any) => {
        const v = getVal(filterKey, row);
        if (v === undefined || v === null) return false;
        return String(v).toLowerCase().indexOf(q) !== -1;
      };
    }

    // Object: JSON stringify once per row/key (value cache already helps)
    return (row: Row, getVal: (k: string, r: Row) => any) => {
      const v = getVal(filterKey, row);
      if (v === undefined || v === null) return false;
      try {
        return JSON.stringify(v).toLowerCase().indexOf(q) !== -1;
      } catch {
        return false;
      }
    };
  }

  // Fast accessor that goes through caches
  private getVal = (key: string, row: Row) => this.extractValueFromPointer(key, row);

  applyFiltersAndSort({
    data,
    order,
    orderBy,
    filters,
    headers,
  }: {
    data: any[];
    order: 'asc' | 'desc';
    orderBy: string;
    filters: { [key: string]: string };
    headers: any[];
  }) {
    if (!Array.isArray(data) || data.length === 0) return [];

    // Prebuild header map & filter predicates once
    const headerMap = this.buildHeaderMap(headers);
    const predicates = Object.entries(filters)
      .filter(([, v]) => v != null && String(v).trim() !== '')
      .map(([k, v]) => this.makeFilterPredicate(k, String(v), headerMap));

    // Decorate rows with keys used for sorting once (also warms caches)
    const keyGetter = this.makeKeyGetter(orderBy);
    const decorated = data.map((row, idx) => ({
      row,
      idx,
      sortKey: keyGetter(row),
    }));

    // Filter pass (fast predicates, cached value access)
    const filtered = predicates.length
      ? decorated.filter(d => {
          for (const p of predicates) {
            if (!p(d.row, this.getVal)) return false;
          }
          return true;
        })
      : decorated;

    // Sort pass (uses precomputed sortKey; stable by idx)
    const dir = order === 'desc' ? -1 : 1;
    const { collator } = this;

    filtered.sort((a, b) => {
      const av = a.sortKey;
      const bv = b.sortKey;
      if (av === bv) return a.idx - b.idx;

      if (av == null) return 1 * dir;
      if (bv == null) return -1 * dir;

      if (typeof av === 'number' && typeof bv === 'number') {
        return (av < bv ? -1 : 1) * dir;
      }
      if (typeof av === 'string' && typeof bv === 'string') {
        const c = collator.compare(av, bv);
        return (c < 0 ? -1 : c > 0 ? 1 : 0) * dir;
      }
      return (av < bv ? -1 : 1) * dir;
    });

    return filtered.map(d => d.row);
  }

  formatDate(date: string) {
    const dt = new Date(date);
    return `${(dt.getMonth() + 1).toString().padStart(2, '0')}/${dt
      .getDate()
      .toString()
      .padStart(2, '0')}/${dt.getFullYear().toString().padStart(4, '0')} ${dt
      .getHours()
      .toString()
      .padStart(2, '0')}:${dt.getMinutes().toString().padStart(2, '0')}:${dt
      .getSeconds()
      .toString()
      .padStart(2, '0')}`;
  }
}

export default TableUtils;
